<template>
  <v-container fluid class="mt-3 pr-8">
  <v-row>
    <v-col xs="24" sm="12" md="6" lg="6">
    <p class="text-h6 font-weight-light text-uppercase">{{ $t('individualVatRecord') }}<v-spacer></v-spacer>
        <v-tooltip right>
            <template v-slot:activator="{ on, attrs }">
             <v-badge
             bordered
             color="error"
             overlap
             dot
             :value="filtered"
             >
               <v-icon 
                 v-bind="attrs"
                 v-on="on" 
                 @click="showSearch = !showSearch">
                   search
               </v-icon>
             </v-badge>
           </template>
           <span>{{ searchLabel }}</span>
        </v-tooltip>
    </p>
    </v-col>
    <v-col xs="24" sm="12" md="6" lg="6" align="right">
    <p>
        <a @click="uploadXlsx()">
            {{ $t('uploadXlsx') }}
        </a>
    </p>
    </v-col>
    </v-row>
    <!--    Row for filters    -->
    <v-row v-show="showSearch">

      <!--   SearchCalculationNumber -->
      <v-col xs="12" sm="6" md="3" lg="3">
        <v-text-field
          :clearable="true"
          dense
          v-bind:label="$t('calculationNumber')"
          outlined
          prepend-inner-icon="search"
          v-model="filter.calculationNumber"
        >
        </v-text-field>
      </v-col>
      <!--   SearchRelatedDocumentNumber  -->
      <v-col xs="12" sm="6" md="3" lg="3">
        <v-text-field
          :clearable="true"
          dense
          v-bind:label="$t('relatedDocumentNumber')"
          outlined
          prepend-inner-icon="search"
          v-model="filter.relatedDocumentNumber"
        >
        </v-text-field>
      </v-col>
      <!--   SearchPeriod  -->
      <v-col xs="12" sm="6" md="3" lg="3">
        <v-select
          v-model="filter.selectedPeriod"
          :clearable="true"
          :items="periodItems"
          :item-text="getVatPeriodText"
          item-value="VatPeriodId"
          v-bind:no-data-text="$t('noData')"
          v-bind:label="$t('period')"
          persistent-hint
          return-object
          multiple
          dense
          outlined
          prepend-inner-icon="search"
        >
        <template v-slot:selection="{item, index }">
          <span v-if="index === 0">{{ getVatPeriodText(item) }}</span>
          <span
            v-if="index === 1"
            class="grey--text text-caption"
          >
            (+{{ filter.selectedPeriod.length - 1 }})
          </span>
        </template>
        </v-select>
      </v-col>      
      <!--   SearchStatus -->
      <v-col xs="12" sm="6" md="3" lg="3">
        <v-select
          v-model="filter.selectedStatus"
          :clearable="true"
          :items="statusItems"
          :item-text="getStatusText"
          item-value="vatRecordStatusId"
          v-bind:no-data-text="$t('noData')"
          v-bind:label="$t('status')"
          persistent-hint
          return-object
          multiple
          dense
          outlined
          prepend-inner-icon="search"
        >
        <template v-slot:selection="{item, index }">
          <span v-if="index === 0">{{ getStatusText(item) }}</span>
          <span
            v-if="index === 1"
            class="grey--text text-caption"
          >
            (+{{ filter.selectedStatus.length - 1 }})
          </span>
        </template>
        </v-select>
      </v-col>
    </v-row>
    <v-row justify="end" no-gutters v-show="showSearch">
      <!--   Filter button   -->
        <v-btn 
          dark
          color="#2b5d66"
          large
          class="filter-button"
          @click="filterRecords()"
          >{{ $t('filter') }}</v-btn
        >

        <v-btn
          dark
          color="#2b5d66"
          large
          class="filter-button"
          @click="clearFilter()"
          >{{ $t('clearFilter') }}</v-btn
        >
    </v-row>

    <!--    VAT Record TABLE VIEW    -->
    <v-row>
        <v-col>
            <v-progress-linear indeterminate v-show="loading"></v-progress-linear>
    
            <v-data-table
            fixed-header
            :height="windowHeight"
            :headers="headers"
            :items="vatRecords"
            item-key="individualVatRecordId"
            class="elevation-1 grey lighten-3"
            v-bind:no-data-text="$t('noData')"
            v-bind:no-results-text="$t('noData')"
            :footer-props="{
                showFirstLastPage: true,
                'items-per-page-text': $t('rowsPerPage'),
            }"
            :items-per-page="itemsPerPage"
            @update:items-per-page="getItemPerPage"
            >
                <!--    Table toolbar    -->
                <template v-slot:top>
                <v-toolbar flat dark color="#2b5d66">
                    <v-toolbar-title>{{ $t('individualVatRecord') }}</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-btn outlined @click="newVatRecord">
                      <v-icon
                        left
                      >
                      note_add
                      </v-icon>
                      {{ $t('addNewDocument') }}
                   </v-btn>
                </v-toolbar>
                </template>
                <!--  Change of pagination. Put 'od' instead 'of'  -->
                <template v-slot:[`footer.page-text`]="items">
                    {{ items.pageStart }} - {{ items.pageStop }} {{ $t('of') }}
                    {{ items.itemsLength }}
                </template>

                <!-- Custom HEADER -->
                <template v-for="h in headers" v-slot:[`header.${h.value}`]="{ header }">
                    {{ $t(header.text) }} 
                </template>

                <!-- Open invoice -->
                <template v-slot:item.calculationNumber="{ item }">
                    <a @click="openDocument(item)">
                    {{ item.calculationNumber }}
                    </a>
                </template>

                <!-- vat period -->
                <template v-slot:item.vatPeriod="{ item }">
                    <span>{{ vatPeriodText(item) }}</span>
                </template>

                <!-- Status -->
                <template v-slot:item.status="{ item }">
                    <span>{{ documentStatusText(item) }}</span>
                </template>

                <!-- DocumentType -->
                <template v-slot:item.documentType="{ item }">
                    <span>{{ documentTypeText(item) }}</span>
                </template>

                <!-- Format amount -->
                <template v-slot:item.totalAmount="{ item }">
                    <span>{{ formatNumberToString(item.totalAmount) }}</span>
                </template>

                <!-- Format VAT -->
                <template v-slot:item.vatAmount="{ item }">
                    <span>{{ formatNumberToString(item.vatAmount) }}</span>
                </template>
            </v-data-table>
        </v-col>
  </v-row>
  </v-container>
</template>
<script>
    export default {
        name: "IndividualVatRecord",
        components: {},
        data() {
            return {
                windowHeight: window.innerHeight - 400,
                loading: false,
                itemsPerPage: 10,
                filtered: false,
                showSearch: false,
                headers: [
                {
                    text: "calculationNumber",
                    sortable: true,
                    value:"calculationNumber"
                },
                {
                    text: "period",
                    sortable: true,
                    value:"vatPeriod"
                },
                {
                    text: "status",
                    sortable: true,
                    value:"status"
                },
                {
                    text: "relatedDocumentNumber",
                    sortable: true,
                    value:"documentNumber"
                },
                {
                    text: "internalInvoiceNumber",
                    sortable: true,
                    value:"internalInvoiceNumber"
                },
                {
                    text: "statusChangeDate",
                    sortable: true,
                    value:"statusChangeDate"
                },
                {
                    text: "documentType",
                    sortable: true,
                    value:"documentType"
                },
                {
                    text: "totalAmount", 
                    sortable: true,
                    value:"totalAmount"
                },
                {
                    text: "vat", 
                    sortable: true,
                    value:"vatAmount"
                }
                ],
                filter: {
                    calculationNumber: "",
                    selectedPeriod: [],
                    relatedDocumentNumber: "",
                    selectedStatus: []
                },
                vatRecords: [],
                statusItems: [],
                periodItems: []
            }
        },
        created() {            
            this.getStatuses();
            this.getPeriods();
            
            if(localStorage.getItem('individuaVatItemsPerPage'))
                this.itemsPerPage = JSON.parse(localStorage.getItem('individuaVatItemsPerPage'));
            if(localStorage.getItem('individualVatFilter')) {
                this.filter = JSON.parse(localStorage.getItem('individualVatFilter'))
                this.filterRecords();
            } 
            else {
                this.getVatRecords();
            }            
        },
        mounted() {
            this.$nextTick(() => {
                window.addEventListener('resize', this.onResize);
            })
        },
        computed: {
            searchLabel() {
                if(this.filtered)
                    return this.$t('filtered');
                else  
                    return this.$t('detailedSearch');
            },
            selectedOrganization() {
                return this.$store.getters.organizationId;
            }
        },
        methods: {
            getVatRecords() {
                var searchRecord = {
                    organizationId: this.selectedOrganization,
                    calculationNumber: null,
                    relatedDocumentNumber: null,
                    period: null,
                    status: null
                }
            
                var formData = new FormData();
                formData.append('searchRecordJson', JSON.stringify(searchRecord));
                this.loading = true;  
                this.axios.post('VatRecord/GetAllIndividualVatRecord', formData).then((response) => {  
                    this.loading = false;  
                    this.vatRecords = response.data;  
                    })
                .catch(err => console.log(err));  
            },
            async filterRecords() {
                var statusFilter = '';
                for(let i = 0; i < this.filter.selectedStatus.length;  i++){
                    statusFilter += this.filter.selectedStatus[i].vatRecordStatusId + ',';
                }
                statusFilter = statusFilter.substring(0, statusFilter.length - 1);

                var periodFilter = '';
                for(let i = 0; i < this.filter.selectedPeriod.length;  i++){
                    periodFilter += this.filter.selectedPeriod[i].vatPeriodId + ',';
                }
                periodFilter = periodFilter.substring(0, periodFilter.length - 1);

                var searchRecord = {
                    organizationId: this.selectedOrganization,
                    calculationNumber: this.filter.calculationNumber,
                    relatedDocumentNumber: this.filter.relatedDocumentNumber,
                    period: periodFilter,
                    status: statusFilter
                }

                var formData = new FormData();
                formData.append('searchRecordJson', JSON.stringify(searchRecord));
                localStorage.setItem('individualVatFilter', JSON.stringify(this.filter));
                this.loading = true;  
                await this.axios.post('VatRecord/GetAllIndividualVatRecord', formData).then((response) => {  
                    this.loading = false;  
                    this.filtered = true;
                    this.vatRecords = response.data;  
                    })
                    .catch(err => console.log(err));

            },
            clearFilter() {
                this.filtered = false
                this.filter = {
                    calculationNumber: "",
                    relatedDocumentNumber: "", 
                    selectedPeriod: [],
                    selectedStatus: []
                }
                localStorage.removeItem('individualVatFilter')
                this.getVatRecords();
            },
            uploadXlsx() {
                this.$router.push({ name: 'IndividualVatRecordExcel'});
            },
            openDocument(item) {
                if(item.old)
                    this.$router.push({ name: 'OldIndividualVatForm', query: {id: item.individualVatRecordId }})
                else
                    this.$router.push({ name: 'IndividualVatForm', query: {id: item.individualVatRecordId }})
            },
            getItemPerPage(val) {
                localStorage.setItem('individuaVatItemsPerPage', JSON.stringify(val))
            },
            newVatRecord() {
                this.$router.push({ name: 'IndividualVatForm'});
            },
            getStatuses(){
                this.axios.get('InvoiceUtilities/GetVatRecordStatuses').then((response) => {
                    this.statusItems = response.data;
                })
                .catch(err => console.log(err));
            },
            getPeriods(){
                this.axios.get('InvoiceUtilities/GetVatPeriod').then((response) => {
                    this.periodItems = response.data;
                })
                .catch(err => console.log(err));
            },
            documentTypeText(item) {
                if(localStorage.getItem('language') === 'eng')
                    return item.documentType.documentTypeEng;
                else
                    return item.documentType.documentType;
            },
            documentStatusText(item) {
                if(localStorage.getItem('language') === 'eng')
                    return item.status.vatRecordStatusEng;
                else
                    return item.status.vatRecordStatus;
            },
            vatPeriodText(item) {
                if(localStorage.getItem('language') === 'eng')
                    return item.period.periodEng;
                else
                    return item.period.periodSrb;
            },
            getStatusText(item) {
                if(localStorage.getItem('language') === 'eng')
                    return item.vatRecordStatusEng;
                else
                    return item.vatRecordStatus;
            },
            getVatPeriodText(item) {
                if(localStorage.getItem('language') === 'eng')
                    return item.periodEng;
                else
                    return item.periodSrb;
            },
            formatNumberToString(value) {
                value = value.toString().replace(",", ".");
                var formatter = new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2});
                return formatter.format(value);
            },
            onResize() {
                this.windowHeight = window.innerHeight - 400
            }
        }
    }
</script>